import { Component, OnInit, OnDestroy, ViewChild, signal, computed, HostListener } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import Swal from 'sweetalert2';
import { 
  CalendarService, 
  CalendarEvent, 
  EVENT_CATEGORIES, 
  REMINDER_OPTIONS,
  CalendarEventColor 
} from './calendar.service';
import { MdbCalendarComponent, MdbCalendarEvent, startOfDay, endOfDay } from 'mdb-angular-calendar';
import { AuthService } from '../services/auth.services';

type ViewMode = 'month' | 'week' | 'list';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  events: CalendarEvent[];
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' }))
      ])
    ])
  ]
})
export class CalendarComponent implements OnInit, OnDestroy {
  @ViewChild('calendar') mdbCalendar!: MdbCalendarComponent;

  private destroy$ = new Subject<void>();
  private userId = '';

  // View state
  readonly currentView = signal<ViewMode>('month');
  readonly selectedDate = signal<Date>(new Date());
  readonly showEventModal = signal<boolean>(false);
  readonly showEventDetails = signal<boolean>(false);
  readonly isEditing = signal<boolean>(false);
  readonly showAdvancedOptions = signal<boolean>(false);
  
  // Calendar data
  events: CalendarEvent[] = [];
  mdbEvents: MdbCalendarEvent[] = [];
  calendarDays: CalendarDay[] = [];
  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Event form data
  eventForm = {
    id: '',
    title: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '10:00',
    allDay: false,
    category: 'work',
    description: '',
    location: '',
    reminder: 15,
  };
  
  // Selected event for details view
  selectedEvent: CalendarEvent | null = null;
  
  // Constants
  readonly categories = EVENT_CATEGORIES;
  readonly reminderOptions = REMINDER_OPTIONS;

  // Computed values
  readonly currentMonthName = computed(() => {
    const date = this.selectedDate();
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  });

  readonly currentMontShorthName = computed(() => {
    const date = this.selectedDate();
    return date.toLocaleString('default', { month: 'short', year: 'numeric' });
  });

  readonly eventsForSelectedDate = computed(() => {
    const date = this.selectedDate();
    return this.calendarService.getEventsForDate(date);
  });

  readonly upcomingEvents = computed(() => {
    return this.calendarService.getUpcomingEvents(5);
  });

  readonly todayEvents = computed(() => {
    return this.calendarService.getEventsForDate(new Date());
  });

  constructor(
    public calendarService: CalendarService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    // Get user ID from auth
    this.auth.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (user?.email) {
        this.userId = user.email;
        this.calendarService.setUserId(this.userId);
      }
    });

    // Subscribe to events
    this.calendarService.events$.pipe(takeUntil(this.destroy$)).subscribe(events => {
      this.events = events;
      this.mdbEvents = this.convertToMdbEvents(events);
      this.generateCalendarDays();
    });

    // Initialize calendar
    this.generateCalendarDays();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Convert to MDB Calendar events format
  private convertToMdbEvents(events: CalendarEvent[]): MdbCalendarEvent[] {
    return events.map(event => ({
      id: event.id,
      summary: event.title,
      startDate: event.startDate,
      endDate: event.endDate,
      allDay: event.allDay || false,
      color: event.color || { background: '#3b82f6', foreground: '#ffffff' },
      description: event.description || '',
    }));
  }

  // Generate calendar grid for month view
  generateCalendarDays(): void {
    const date = this.selectedDate();
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      days.push({
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === month,
        isToday: currentDate.getTime() === today.getTime(),
        isSelected: currentDate.toDateString() === date.toDateString(),
        events: this.calendarService.getEventsForDate(currentDate),
      });
    }
    
    this.calendarDays = days;
  }

  // View controls
  setView(view: ViewMode): void {
    this.currentView.set(view);
    this.showViewDropdown = false;
  }

  toggleViewDropdown(): void {
    this.showViewDropdown = !this.showViewDropdown;
  }

  closeViewDropdown(): void {
    this.showViewDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.view-dropdown-container')) {
      this.showViewDropdown = false;
    }
  }

  goToToday(): void {
    this.selectedDate.set(new Date());
    this.generateCalendarDays();
  }

  navigate(direction: 'prev' | 'next'): void {
    const current = this.selectedDate();
    const view = this.currentView();
    
    if (view === 'month') {
      const newDate = new Date(current.getFullYear(), current.getMonth() + (direction === 'next' ? 1 : -1), 1);
      this.selectedDate.set(newDate);
    } else if (view === 'week') {
      const newDate = new Date(current);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
      this.selectedDate.set(newDate);
    }
    
    this.generateCalendarDays();
  }

  selectDate(day: CalendarDay): void {
    this.selectedDate.set(day.date);
    this.generateCalendarDays();
  }

  // Event Modal Controls
  openAddEventModal(date?: Date): void {
    this.isEditing.set(false);
    this.resetEventForm();
    
    const targetDate = date || this.selectedDate();
    this.eventForm.startDate = this.formatDateForInput(targetDate);
    this.eventForm.endDate = this.formatDateForInput(targetDate);
    
    this.showEventModal.set(true);
  }

  openEditEventModal(event: CalendarEvent): void {
    this.isEditing.set(true);
    this.eventForm = {
      id: event.id,
      title: event.title,
      startDate: this.formatDateForInput(event.startDate),
      startTime: this.formatTimeForInput(event.startDate),
      endDate: this.formatDateForInput(event.endDate),
      endTime: this.formatTimeForInput(event.endDate),
      allDay: event.allDay || false,
      category: event.category || 'work',
      description: event.description || '',
      location: event.location || '',
      reminder: event.reminder || 15,
    };
    
    this.showEventDetails.set(false);
    this.showEventModal.set(true);
  }

  closeEventModal(): void {
    this.showEventModal.set(false);
    this.resetEventForm();
  }

  openEventDetails(event: CalendarEvent): void {
    this.selectedEvent = event;
    this.showEventDetails.set(true);
  }

  closeEventDetails(): void {
    this.showEventDetails.set(false);
    this.selectedEvent = null;
  }

  private resetEventForm(): void {
    this.eventForm = {
      id: '',
      title: '',
      startDate: this.formatDateForInput(new Date()),
      startTime: '09:00',
      endDate: this.formatDateForInput(new Date()),
      endTime: '10:00',
      allDay: false,
      category: 'work',
      description: '',
      location: '',
      reminder: 15,
    };
  }

  // Event CRUD
  saveEvent(): void {
    if (!this.eventForm.title.trim()) {
      Swal.fire({
        title: 'Missing Title',
        text: 'Please enter an event title.',
        icon: 'warning',
        background: '#1a1a2e',
        color: '#fff',
      });
      return;
    }

    const startDateTime = this.combineDateAndTime(this.eventForm.startDate, this.eventForm.allDay ? '00:00' : this.eventForm.startTime);
    const endDateTime = this.combineDateAndTime(this.eventForm.endDate, this.eventForm.allDay ? '23:59' : this.eventForm.endTime);

    if (endDateTime < startDateTime) {
      Swal.fire({
        title: 'Invalid Dates',
        text: 'End date/time must be after start date/time.',
        icon: 'error',
        background: '#1a1a2e',
        color: '#fff',
      });
      return;
    }

    const categoryColor = this.calendarService.getCategoryColor(this.eventForm.category);

    const eventData = {
      title: this.eventForm.title.trim(),
      startDate: startDateTime,
      endDate: endDateTime,
      allDay: this.eventForm.allDay,
      category: this.eventForm.category,
      color: categoryColor,
      description: this.eventForm.description.trim(),
      location: this.eventForm.location.trim(),
      reminder: this.eventForm.reminder,
    };

    if (this.isEditing()) {
      this.calendarService.updateEvent(this.eventForm.id, eventData)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.showSuccessMessage('Event updated successfully!');
          this.closeEventModal();
          this.generateCalendarDays();
        });
    } else {
      this.calendarService.addEvent(eventData)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.showSuccessMessage('Event created successfully!');
          this.closeEventModal();
          this.generateCalendarDays();
        });
    }
  }

  async deleteEvent(event: CalendarEvent): Promise<void> {
    const result = await Swal.fire({
      title: 'Delete Event?',
      text: `Are you sure you want to delete "${event.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      background: '#1a1a2e',
      color: '#fff',
    });

    if (result.isConfirmed) {
      this.calendarService.deleteEvent(event.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.showSuccessMessage('Event deleted successfully!');
          this.closeEventDetails();
          this.generateCalendarDays();
        });
    }
  }

  // MDB Calendar event handlers
  onEventClick(event: MdbCalendarEvent): void {
    const calendarEvent = this.events.find(e => e.id === event.id);
    if (calendarEvent) {
      this.openEventDetails(calendarEvent);
    }
  }

  onEventDragEnd(event: { event: MdbCalendarEvent; newStartDate: Date; newEndDate: Date }): void {
    this.calendarService.updateEvent(event.event.id as string, {
      startDate: event.newStartDate,
      endDate: event.newEndDate,
    }).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.generateCalendarDays();
    });
  }

  onDateClick(date: Date): void {
    this.selectedDate.set(date);
    this.generateCalendarDays();
  }

  // Export dropdown state
  showExportMenu = false;
  showViewDropdown = false;

  // Export/Import
  exportEvents(): void {
    const json = this.calendarService.exportEvents();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendar-events-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    this.showSuccessMessage('Events exported successfully!');
  }

  toggleExportMenu(): void {
    this.showExportMenu = !this.showExportMenu;
  }

  closeExportMenu(): void {
    this.showExportMenu = false;
  }

  // Export all events to ICS format
  exportToICS(): void {
    const icsContent = this.generateICSFile(this.events);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendar-events-${new Date().toISOString().split('T')[0]}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    this.closeExportMenu();
    this.showSuccessMessage('Events exported to ICS format!');
  }

  // Export single event to ICS
  exportEventToICS(event: CalendarEvent): void {
    const icsContent = this.generateICSFile([event]);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Generate ICS file content
  private generateICSFile(events: CalendarEvent[]): string {
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EWS Calendar//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ];

    for (const event of events) {
      lines.push(...this.generateICSEvent(event));
    }

    lines.push('END:VCALENDAR');
    return lines.join('\r\n');
  }

  private generateICSEvent(event: CalendarEvent): string[] {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    const uid = `${event.id}@ews-calendar`;
    
    const lines = [
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${this.formatICSDate(new Date())}`,
    ];

    if (event.allDay) {
      lines.push(`DTSTART;VALUE=DATE:${this.formatICSDateOnly(startDate)}`);
      lines.push(`DTEND;VALUE=DATE:${this.formatICSDateOnly(endDate)}`);
    } else {
      lines.push(`DTSTART:${this.formatICSDate(startDate)}`);
      lines.push(`DTEND:${this.formatICSDate(endDate)}`);
    }

    lines.push(`SUMMARY:${this.escapeICSText(event.title)}`);
    
    if (event.description) {
      lines.push(`DESCRIPTION:${this.escapeICSText(event.description)}`);
    }
    
    if (event.location) {
      lines.push(`LOCATION:${this.escapeICSText(event.location)}`);
    }

    if (event.reminder && event.reminder > 0) {
      lines.push('BEGIN:VALARM');
      lines.push('ACTION:DISPLAY');
      lines.push(`TRIGGER:-PT${event.reminder}M`);
      lines.push(`DESCRIPTION:${this.escapeICSText(event.title)}`);
      lines.push('END:VALARM');
    }

    lines.push('END:VEVENT');
    return lines;
  }

  private formatICSDate(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }

  private formatICSDateOnly(date: Date): string {
    return date.toISOString().split('T')[0].replace(/-/g, '');
  }

  private escapeICSText(text: string): string {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  }

  // Add event to Google Calendar
  addToGoogleCalendar(event: CalendarEvent): void {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${this.formatGoogleDate(startDate)}/${this.formatGoogleDate(endDate)}`,
    });

    if (event.description) {
      params.append('details', event.description);
    }
    if (event.location) {
      params.append('location', event.location);
    }

    const url = `https://calendar.google.com/calendar/render?${params.toString()}`;
    window.open(url, '_blank');
    this.closeExportMenu();
  }

  private formatGoogleDate(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }

  // Add event to Yahoo Calendar
  addToYahooCalendar(event: CalendarEvent): void {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    const duration = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60)); // in minutes
    const hours = Math.floor(duration / 60).toString().padStart(2, '0');
    const minutes = (duration % 60).toString().padStart(2, '0');

    const params = new URLSearchParams({
      v: '60',
      title: event.title,
      st: this.formatYahooDate(startDate),
      dur: `${hours}${minutes}`,
    });

    if (event.description) {
      params.append('desc', event.description);
    }
    if (event.location) {
      params.append('in_loc', event.location);
    }

    const url = `https://calendar.yahoo.com/?${params.toString()}`;
    window.open(url, '_blank');
    this.closeExportMenu();
  }

  private formatYahooDate(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z/, '');
  }

  // Add event to Outlook Calendar (Web)
  addToOutlookCalendar(event: CalendarEvent): void {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);

    const params = new URLSearchParams({
      rru: 'addevent',
      subject: event.title,
      startdt: startDate.toISOString(),
      enddt: endDate.toISOString(),
      allday: event.allDay ? 'true' : 'false',
    });

    if (event.description) {
      params.append('body', event.description);
    }
    if (event.location) {
      params.append('location', event.location);
    }

    const url = `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
    window.open(url, '_blank');
    this.closeExportMenu();
  }

  // Add event to Office 365 Outlook
  addToOffice365Calendar(event: CalendarEvent): void {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);

    const params = new URLSearchParams({
      rru: 'addevent',
      subject: event.title,
      startdt: startDate.toISOString(),
      enddt: endDate.toISOString(),
      allday: event.allDay ? 'true' : 'false',
    });

    if (event.description) {
      params.append('body', event.description);
    }
    if (event.location) {
      params.append('location', event.location);
    }

    const url = `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
    window.open(url, '_blank');
    this.closeExportMenu();
  }

  // Show export options for a single event
  async showEventExportOptions(event: CalendarEvent): Promise<void> {
    const { value: choice } = await Swal.fire({
      title: 'Add to Calendar',
      html: `<p style="color: #a89cc4; margin-bottom: 16px;">Choose where to add "${event.title}"</p>`,
      input: 'select',
      inputOptions: {
        'google': '📅 Google Calendar',
        'yahoo': '📆 Yahoo Calendar',
        'outlook': '📧 Outlook.com',
        'office365': '💼 Office 365',
        'ics': '📁 Download ICS File',
      },
      inputPlaceholder: 'Select a calendar',
      showCancelButton: true,
      confirmButtonText: 'Add',
      background: '#1a1a2e',
      color: '#fff',
      customClass: {
        input: 'swal-select-dark',
      },
    });

    if (choice) {
      switch (choice) {
        case 'google':
          this.addToGoogleCalendar(event);
          break;
        case 'yahoo':
          this.addToYahooCalendar(event);
          break;
        case 'outlook':
          this.addToOutlookCalendar(event);
          break;
        case 'office365':
          this.addToOffice365Calendar(event);
          break;
        case 'ics':
          this.exportEventToICS(event);
          break;
      }
    }
  }

  async importEvents(): Promise<void> {
    const { value: file } = await Swal.fire({
      title: 'Import Events',
      input: 'file',
      inputAttributes: {
        accept: '.json',
        'aria-label': 'Upload your events file',
      },
      showCancelButton: true,
      confirmButtonText: 'Import',
      background: '#1a1a2e',
      color: '#fff',
    });

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const json = e.target?.result as string;
        const success = this.calendarService.importEvents(json);
        
        if (success) {
          this.showSuccessMessage('Events imported successfully!');
          this.generateCalendarDays();
        } else {
          Swal.fire({
            title: 'Import Failed',
            text: 'The file could not be parsed.',
            icon: 'error',
            background: '#1a1a2e',
            color: '#fff',
          });
        }
      };
      reader.readAsText(file);
    }
  }

  // Utility methods
  private formatDateForInput(date: Date): string {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  private formatTimeForInput(date: Date): string {
    const d = new Date(date);
    return d.toTimeString().slice(0, 5);
  }

  private combineDateAndTime(dateStr: string, timeStr: string): Date {
    return new Date(`${dateStr}T${timeStr}:00`);
  }

  private showSuccessMessage(message: string): void {
    Swal.fire({
      title: 'Success',
      text: message,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      background: '#1a1a2e',
      color: '#fff',
    });
  }

  formatEventTime(event: CalendarEvent): string {
    if (event.allDay) {
      return 'All day';
    }
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    return `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }

  formatEventDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }

  getCategoryName(categoryId: string): string {
    return this.categories.find(c => c.id === categoryId)?.name || 'Other';
  }

  getCategoryColor(categoryId: string): CalendarEventColor {
    return this.calendarService.getCategoryColor(categoryId);
  }

  getWeekDates(): Date[] {
    const current = this.selectedDate();
    const startOfWeek = new Date(current);
    startOfWeek.setDate(current.getDate() - current.getDay());
    
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  trackByEventId(index: number, event: CalendarEvent): string {
    return event.id;
  }

  trackByDate(index: number, day: CalendarDay): number {
    return day.date.getTime();
  }

  getEventHour(startDate: Date): number {
    return new Date(startDate).getHours();
  }

  getReminderLabel(reminder: number): string {
    const option = this.reminderOptions.find(r => r.value === reminder);
    return option?.label || 'No reminder';
  }
}
